<script setup>
/* global axios */
import { ref, computed, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const store = useStore();

const inboxName = ref('');
const acceptedDisclaimer = ref(false);
const channelId = ref(null);
const inboxId = ref(null);
const qrBase64 = ref(null);
const sessionStatus = ref(null);
const connectedPhone = ref(null);
const isStarting = ref(false);
const errorMessage = ref('');

let pollHandle = null;

const accountId = computed(() => route.params.accountId);

const canStart = computed(
  () => inboxName.value.trim().length > 0 && acceptedDisclaimer.value
);

const isAuthenticated = computed(() => sessionStatus.value === 'WORKING');

const failureCount = ref(0);
const MAX_CONSECUTIVE_FAILURES = 5;

function stopPolling() {
  if (pollHandle) {
    clearInterval(pollHandle);
    pollHandle = null;
  }
}

function proceedToAgentStep() {
  store.dispatch('inboxes/get');
  router.replace({
    name: 'settings_inboxes_add_agents',
    params: { page: 'new', inbox_id: inboxId.value },
  });
}

const pollQr = async () => {
  if (!channelId.value) return;
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/channels/whatsapp/waha/qr/${channelId.value}`
    );
    failureCount.value = 0;
    sessionStatus.value = data.session_status;
    qrBase64.value = data.qr_base64;
    connectedPhone.value = data.connected_phone;
    if (isAuthenticated.value) {
      stopPolling();
      proceedToAgentStep();
    }
  } catch (e) {
    if (e.response?.status === 401) {
      stopPolling();
      errorMessage.value = t('INBOX_MGMT.ADD.WHATSAPP.WAHA.AUTH_EXPIRED');
      return;
    }
    failureCount.value += 1;
    if (failureCount.value >= MAX_CONSECUTIVE_FAILURES) {
      stopPolling();
      errorMessage.value = t('INBOX_MGMT.ADD.WHATSAPP.WAHA.POLL_FAILED');
    }
  }
};

function schedulePoll() {
  stopPolling();
  pollQr();
  pollHandle = setInterval(pollQr, 2000);
}

const startSession = async () => {
  errorMessage.value = '';
  isStarting.value = true;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId.value}/channels/whatsapp/waha/start`,
      { name: inboxName.value }
    );
    channelId.value = data.channel_id;
    inboxId.value = data.inbox_id;
    sessionStatus.value = data.session_status;
    schedulePoll();
  } catch (e) {
    const msg =
      e.response?.data?.error || e.message || 'Failed to start session';
    errorMessage.value = msg;
    useAlert(msg);
  } finally {
    isStarting.value = false;
  }
};

const cancelSession = async () => {
  stopPolling();
  if (!channelId.value) {
    router.go(-1);
    return;
  }
  try {
    await axios.delete(
      `/api/v1/accounts/${accountId.value}/channels/whatsapp/waha/disconnect/${channelId.value}`
    );
  } catch {
    // ignore — orphan rows cleaned up by health check
  }
  router.go(-1);
};

onBeforeUnmount(() => {
  stopPolling();
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <div>
      <h2 class="text-xl font-semibold text-n-slate-12">
        {{ t('INBOX_MGMT.ADD.WHATSAPP.WAHA.TITLE') }}
      </h2>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.ADD.WHATSAPP.WAHA.SUBTITLE') }}
      </p>
    </div>

    <div
      v-if="!channelId"
      class="flex flex-col gap-4 p-4 rounded-lg bg-n-amber-3 border border-n-amber-6"
    >
      <h3 class="text-sm font-medium text-n-amber-12">
        {{ t('INBOX_MGMT.ADD.WHATSAPP.WAHA.DISCLAIMER_TITLE') }}
      </h3>
      <p class="text-sm text-n-amber-11">
        {{ t('INBOX_MGMT.ADD.WHATSAPP.WAHA.DISCLAIMER_BODY') }}
      </p>
      <label class="flex items-start gap-2 text-sm text-n-amber-12">
        <input v-model="acceptedDisclaimer" type="checkbox" class="mt-1" />
        <span>{{ t('INBOX_MGMT.ADD.WHATSAPP.WAHA.DISCLAIMER_ACCEPT') }}</span>
      </label>
    </div>

    <div v-if="!channelId" class="flex flex-col gap-3">
      <Input
        v-model="inboxName"
        :label="t('INBOX_MGMT.ADD.WHATSAPP.WAHA.INBOX_NAME_LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.WHATSAPP.WAHA.INBOX_NAME_PLACEHOLDER')"
      />
      <div class="flex gap-3">
        <Button
          variant="faded"
          color="slate"
          :label="t('INBOX_MGMT.ADD.WHATSAPP.WAHA.CANCEL')"
          @click="router.go(-1)"
        />
        <Button
          :label="t('INBOX_MGMT.ADD.WHATSAPP.WAHA.GENERATE_QR')"
          :disabled="!canStart || isStarting"
          :is-loading="isStarting"
          @click="startSession"
        />
      </div>
    </div>

    <div
      v-else-if="!isAuthenticated"
      class="flex flex-col items-center gap-4 p-6 border border-n-weak rounded-lg"
    >
      <p class="text-sm text-n-slate-11">
        {{
          t('INBOX_MGMT.ADD.WHATSAPP.WAHA.SCAN_INSTRUCTIONS', {
            status: sessionStatus || '...',
          })
        }}
      </p>

      <div v-if="qrBase64" class="p-4 bg-white rounded-lg border border-n-weak">
        <img
          :src="`data:image/png;base64,${qrBase64}`"
          alt="WhatsApp QR Code"
          class="w-64 h-64"
        />
      </div>
      <div v-else class="flex items-center gap-2 text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.ADD.WHATSAPP.WAHA.WAITING_QR') }}
      </div>

      <Button
        variant="faded"
        color="slate"
        :label="t('INBOX_MGMT.ADD.WHATSAPP.WAHA.CANCEL')"
        @click="cancelSession"
      />
    </div>

    <div
      v-if="errorMessage"
      class="p-3 rounded-md bg-n-ruby-3 text-sm text-n-ruby-11 border border-n-ruby-6"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>
